import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { toast } from "sonner";
import { createOrder, getConfig, getPricing, getReferral, meAddresses, validateCoupon } from "../lib/api";
import { couponAmount, couponLabel } from "../lib/coupons";
import { useAuth } from "../lib/auth";

function StepDot({ n, active, done }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center font-display text-sm border ${
          active
            ? "bg-rw-ink text-white border-rw-ink"
            : done
            ? "bg-rw-blue text-rw-ink border-rw-blue"
            : "bg-rw-bg2 text-rw-ink2 border-rw-ink/20"
        }`}
      >
        {n}
      </div>
    </div>
  );
}

export default function Book() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pricing, setPricing] = useState([]);
  const [config, setConfig] = useState(null);
  const [items, setItems] = useState({});
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [savedAddrs, setSavedAddrs] = useState([]);

  const [form, setForm] = useState({
    customer_name: "",
    phone: "",
    address: "",
    sector: "",
    pickup_date: "",
    pickup_slot: "",
    notes: "",
    referral_code: "",
    coupon_code: "",
    use_credit: false,
    lat: null,
    lng: null,
  });
  const [creditInfo, setCreditInfo] = useState(null); // { code, credit_balance }
  const [geoLoading, setGeoLoading] = useState(false);

  const captureLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Your browser doesn't support GPS");
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, lat: pos.coords.latitude, lng: pos.coords.longitude }));
        toast.success("Location captured — the rider will get an exact pin");
        setGeoLoading(false);
      },
      (err) => {
        toast.error(err.code === 1 ? "Location permission denied" : "Could not get location");
        setGeoLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  };

  useEffect(() => {
    Promise.all([getPricing(), getConfig()]).then(([p, c]) => {
      setPricing(p.services || []);
      setConfig(c);
      setForm((f) => ({
        ...f,
        pickup_slot: (c.pickup_slots || [])[0] || "",
        sector: (c.sectors || [])[0] || "",
      }));
    });
    // Prefill from user + load saved addresses
    meAddresses().then((r) => {
      const list = r.addresses || [];
      setSavedAddrs(list);
      const def = list.find((a) => a.is_default) || list[0];
      if (def) {
        setForm((f) => ({ ...f, address: def.address, sector: def.sector || f.sector }));
      }
    }).catch(() => {});
    // Deep-link: ?ref=CODE or ?sector=Sector%2043 (from a sector landing page)
    // Referral: skip auto-fill if the code turns out to be the logged-in user's OWN code.
    (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const sectorParam = params.get("sector");
        if (sectorParam) {
          setForm((f) => ({ ...f, sector: sectorParam }));
        }
        const ref = (params.get("ref") || localStorage.getItem("rw_pending_ref") || "").toUpperCase();
        localStorage.removeItem("rw_pending_ref");
        if (ref) {
          const phoneVal = (user?.phone || "").trim();
          if (phoneVal.length >= 6) {
            try {
              const own = await getReferral(phoneVal);
              if (own?.code === ref) {
                toast.info("That's your own code — share it with a friend instead.");
                return;
              }
            } catch { /* fall through to accept */ }
          }
          setForm((f) => ({ ...f, referral_code: ref }));
          toast.success(`Referral code ${ref} applied`);
        }
        // Deep-link: ?coupon=FIRST50 (from WhatsApp/Instagram promos) — auto-validate + apply
        const couponParam = (params.get("coupon") || "").toUpperCase();
        if (couponParam) {
          try {
            const r = await validateCoupon(couponParam, (user?.phone || "").trim());
            setForm((f) => ({ ...f, coupon_code: r.code }));
            setCouponInfo(r);
            toast.success(`Coupon ${r.code} applied — ${couponLabel(r)} at checkout`, { id: `coupon-${r.code}` });
          } catch (e) {
            setForm((f) => ({ ...f, coupon_code: couponParam }));
            toast.error(e?.response?.data?.detail || `Coupon ${couponParam} is not valid`, { id: `coupon-${couponParam}` });
          }
        }
      } catch { /* noop */ }
    })();
  }, []);

  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      customer_name: f.customer_name || user.name || "",
      phone: f.phone || user.phone || "",
    }));
  }, [user]);

  useEffect(() => {
    // default pickup date = tomorrow
    const d = new Date();
    d.setDate(d.getDate() + 1);
    const iso = d.toISOString().slice(0, 10);
    setForm((f) => ({ ...f, pickup_date: f.pickup_date || iso }));
  }, []);

  const setQty = (id, delta, absolute = null) => {
    setItems((prev) => {
      const cur = prev[id] || 0;
      const next = absolute !== null ? absolute : Math.max(0, cur + delta);
      const clean = { ...prev };
      if (next <= 0) delete clean[id];
      else clean[id] = next;
      return clean;
    });
  };

  const [couponInfo, setCouponInfo] = useState(null); // { code, value } once applied

  const summary = useMemo(() => {
    const rows = pricing
      .filter((p) => items[p.id])
      .map((p) => ({
        service_id: p.id,
        name: p.name,
        unit: p.unit,
        qty: items[p.id],
        unit_price: p.price,
        subtotal: items[p.id] * p.price,
      }));
    const subtotal = rows.reduce((s, r) => s + r.subtotal, 0);
    const fee = subtotal >= 299 || subtotal === 0 ? 0 : 49;
    // Client-side referral discount preview (server is authoritative)
    let discount = 0;
    if (form.referral_code && subtotal > 0) {
      discount = Math.min(Math.round(subtotal * 0.15 * 100) / 100, 100);
    }
    let coupon = 0;
    if (couponInfo) {
      coupon = couponAmount(couponInfo, subtotal + fee - discount);
    }
    let credit = 0;
    if (form.use_credit && creditInfo) {
      credit = Math.min(creditInfo.credit_balance, Math.max(0, subtotal + fee - discount - coupon));
    }
    const total = Math.max(0, subtotal + fee - discount - coupon - credit);
    return { rows, subtotal, fee, discount, coupon, credit, total };
  }, [items, pricing, form.referral_code, form.use_credit, creditInfo, couponInfo]);

  const applyCoupon = async () => {
    const code = (form.coupon_code || "").trim();
    if (!code) { setCouponInfo(null); return; }
    try {
      const r = await validateCoupon(code, (form.phone || user?.phone || "").trim());
      setCouponInfo(r);
      toast.success(`Coupon ${r.code} applied — ${couponLabel(r)}`);
    } catch (e) {
      setCouponInfo(null);
      toast.error(e?.response?.data?.detail || "Invalid coupon");
    }
  };

  const fetchCredit = async () => {
    const phoneVal = (form.phone || user?.phone || "").trim();
    if (phoneVal.length < 6) {
      toast.error("Add your phone first");
      return;
    }
    try {
      const r = await getReferral(phoneVal);
      setCreditInfo(r);
      toast.success(`Your code: ${r.code}  ·  Credit balance: ₹${r.credit_balance}`);
    } catch (e) {
      toast.error("Could not fetch credit");
    }
  };

  const canNext1 = summary.rows.length > 0;
  const canNext2 =
    form.customer_name.trim().length >= 2 &&
    form.phone.trim().length >= 6 &&
    form.address.trim().length >= 5 &&
    form.pickup_date &&
    form.pickup_slot;

  const submit = async () => {
    if (!canNext1 || !canNext2) return;
    setSubmitting(true);
    try {
      const payload = {
        customer_name: form.customer_name.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        sector: form.sector || null,
        pickup_date: form.pickup_date,
        pickup_slot: form.pickup_slot,
        notes: form.notes || null,
        referral_code: form.referral_code.trim() || null,
        coupon_code: form.coupon_code.trim() || null,
        use_credit: !!form.use_credit,
        lat: form.lat,
        lng: form.lng,
        items: summary.rows.map((r) => ({
          service_id: r.service_id,
          name: r.name,
          unit: r.unit,
          qty: r.qty,
          unit_price: r.unit_price,
        })),
      };
      const res = await createOrder(payload);
      toast.success("Pickup scheduled! Order code: " + res.order.order_code);
      navigate("/thank-you", { state: { kind: "order", ...res } });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-rw-bg text-rw-ink" data-testid="booking-page">
      <Seo
        title="Book a Laundry Pickup"
        description="Schedule a free doorstep laundry pickup in Gurugram. Pick your services (wash & fold, ironing, dry cleaning, shoe cleaning), choose a slot and pay via UPI or cash on delivery."
        path="/book"
      />
      <Nav />

      <div className="max-w-6xl mx-auto px-6 md:px-10 pt-12 md:pt-16">
        <div className="overline mb-3">Book pickup</div>
        <h1 className="font-display text-4xl md:text-6xl font-light tracking-tight leading-[1.05]">
          Tell us what needs<br /> a little <em className="italic text-rw-blue not-italic">love</em>.
        </h1>
      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 grid md:grid-cols-5 gap-8">
        <div className="md:col-span-3 space-y-6">
          <div className="flex items-center gap-4">
            <StepDot n={1} active={step === 1} done={step > 1} />
            <div className="text-sm font-semibold">Select services</div>
            <div className="flex-1 h-px bg-rw-ink/10" />
            <StepDot n={2} active={step === 2} done={step > 2} />
            <div className="text-sm font-semibold">Pickup details</div>
          </div>

          {step === 1 && (
            <div className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 divide-y divide-rw-ink/10" data-testid="step-1-services">
              {pricing.map((p) => (
                <div key={p.id} className="p-5 flex items-center gap-4">
                  <div className="flex-1">
                    <div className="font-display text-lg md:text-xl">{p.name}</div>
                    <div className="text-sm text-rw-ink2">
                      {p.original_price > p.price && <span className="line-through text-rw-ink3 mr-1.5">₹{p.original_price}</span>}
                      ₹{p.price} · {p.unit} · {p.turnaround}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setQty(p.id, -1)}
                      className="w-9 h-9 rounded-full border border-rw-ink/20 text-lg hover:bg-rw-bg3"
                      data-testid={`qty-dec-${p.id}`}
                      aria-label={`decrease ${p.name}`}
                    >−</button>
                    <input
                      type="number"
                      min="0"
                      step={p.unit === "per kg" ? "0.5" : "1"}
                      value={items[p.id] || 0}
                      onChange={(e) => setQty(p.id, 0, Math.max(0, parseFloat(e.target.value || 0)))}
                      className="w-16 text-center py-1.5 rounded-md border border-rw-ink/20 bg-white rw-focus"
                      data-testid={`qty-input-${p.id}`}
                    />
                    <button
                      onClick={() => setQty(p.id, p.unit === "per kg" ? 0.5 : 1)}
                      className="w-9 h-9 rounded-full border border-rw-ink/20 text-lg hover:bg-rw-bg3"
                      data-testid={`qty-inc-${p.id}`}
                      aria-label={`increase ${p.name}`}
                    >+</button>
                  </div>
                </div>
              ))}
              <div className="p-5 flex items-center justify-between">
                <Link to="/" className="text-sm text-rw-ink2 hover:text-rw-ink">← Back to home</Link>
                <button
                  onClick={() => setStep(2)}
                  disabled={!canNext1}
                  className="btn-dark rounded-full px-6 py-2.5 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="step-1-next-btn"
                >
                  Continue →
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6 space-y-4" data-testid="step-2-details">
              <div className="grid md:grid-cols-2 gap-4">
                <label className="block">
                  <span className="overline">Your name</span>
                  <input
                    className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus"
                    value={form.customer_name}
                    onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                    placeholder="Ananya Sharma"
                    data-testid="input-name"
                  />
                </label>
                <label className="block">
                  <span className="overline">Phone (WhatsApp)</span>
                  <input
                    className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+91 98xxx xxxxx"
                    data-testid="input-phone"
                  />
                </label>
              </div>
              <label className="block">
                <span className="overline">Pickup address</span>
                <textarea
                  className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus"
                  rows={2}
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="Flat / building / street"
                  data-testid="input-address"
                />
                {savedAddrs.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2" data-testid="saved-addresses">
                    {savedAddrs.map((a) => (
                      <button
                        type="button"
                        key={a.id}
                        onClick={() => setForm((f) => ({ ...f, address: a.address, sector: a.sector || f.sector }))}
                        className="text-xs px-3 py-1.5 rounded-full border border-rw-ink/20 hover:bg-rw-bg3"
                        data-testid={`saved-addr-${a.id}`}
                      >
                        {a.label} · {a.sector || "Address"}
                      </button>
                    ))}
                  </div>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={captureLocation}
                    disabled={geoLoading}
                    className={`text-xs px-3 py-1.5 rounded-full border ${
                      form.lat != null ? "bg-rw-mint border-rw-ink/20" : "border-rw-ink/20 hover:bg-rw-bg3"
                    } disabled:opacity-40`}
                    data-testid="capture-location-btn"
                  >
                    {geoLoading ? "Getting location…" : form.lat != null ? "✓ Location pinned" : "📍 Use my current location"}
                  </button>
                  {form.lat != null && (
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, lat: null, lng: null }))}
                      className="text-xs text-rw-ink3 hover:text-rw-ink"
                      data-testid="clear-location-btn"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </label>
              <div className="grid md:grid-cols-3 gap-4">
                <label className="block">
                  <span className="overline">Sector / area</span>
                  <select
                    className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus"
                    value={form.sector}
                    onChange={(e) => setForm({ ...form, sector: e.target.value })}
                    data-testid="input-sector"
                  >
                    {(config?.sectors || []).map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="overline">Pickup date</span>
                  <input
                    type="date"
                    className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus"
                    value={form.pickup_date}
                    onChange={(e) => setForm({ ...form, pickup_date: e.target.value })}
                    min={new Date().toISOString().slice(0, 10)}
                    data-testid="input-date"
                  />
                </label>
                <label className="block">
                  <span className="overline">Slot</span>
                  <select
                    className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus"
                    value={form.pickup_slot}
                    onChange={(e) => setForm({ ...form, pickup_slot: e.target.value })}
                    data-testid="input-slot"
                  >
                    {(config?.pickup_slots || []).map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="block">
                <span className="overline">Notes (optional)</span>
                <textarea
                  className="mt-1.5 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2.5 rw-focus"
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Stain on white shirt, urgent, etc."
                  data-testid="input-notes"
                />
              </label>

              {/* Referral + Credit */}
              <div className="rounded-xl border border-dashed border-rw-ink/20 bg-rw-bg p-4 space-y-3">
                <div className="overline">Referral, coupon & credit</div>
                <div className="grid md:grid-cols-3 gap-3">
                  <div className="block">
                    <span className="text-xs text-rw-ink2">Coupon — first order? Try FIRST50</span>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        className="w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 rw-focus font-mono uppercase text-sm"
                        value={form.coupon_code}
                        onChange={(e) => { setForm({ ...form, coupon_code: e.target.value.toUpperCase() }); setCouponInfo(null); }}
                        placeholder="FIRST50"
                        data-testid="input-coupon"
                      />
                      <button
                        type="button"
                        onClick={applyCoupon}
                        className="text-xs px-3 py-2 rounded-md border border-rw-ink/20 hover:bg-rw-bg3 whitespace-nowrap"
                        data-testid="apply-coupon-btn"
                      >
                        Apply
                      </button>
                    </div>
                    {couponInfo && <span className="text-xs text-rw-sage" data-testid="coupon-applied">{couponLabel(couponInfo)} applied</span>}
                  </div>
                  <label className="block">
                    <span className="text-xs text-rw-ink2">Have a code? Get 15% off (up to ₹100)</span>
                    <input
                      className="mt-1 w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 rw-focus font-mono uppercase text-sm"
                      value={form.referral_code}
                      onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
                      placeholder="RIZZ-XXXXXX"
                      data-testid="input-referral"
                    />
                  </label>
                  <div className="block">
                    <span className="text-xs text-rw-ink2">Use your credit balance</span>
                    <div className="mt-1 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={fetchCredit}
                        className="text-xs px-3 py-2 rounded-md border border-rw-ink/20 hover:bg-rw-bg3"
                        data-testid="check-credit-btn"
                      >
                        Check my credit
                      </button>
                      {creditInfo && (
                        <label className="flex items-center gap-2 text-sm" data-testid="credit-toggle-wrap">
                          <input
                            type="checkbox"
                            checked={form.use_credit}
                            onChange={(e) => setForm({ ...form, use_credit: e.target.checked })}
                            data-testid="credit-toggle"
                          />
                          Apply ₹{creditInfo.credit_balance}
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <button
                  onClick={() => setStep(1)}
                  className="text-sm text-rw-ink2 hover:text-rw-ink"
                  data-testid="step-2-back-btn"
                >← Back to services</button>
                <button
                  onClick={submit}
                  disabled={!canNext2 || submitting}
                  className="btn-dark rounded-full px-6 py-2.5 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="submit-order-btn"
                >
                  {submitting ? "Scheduling..." : "Confirm pickup →"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Summary sidebar */}
        <aside className="md:col-span-2">
          <div className="sticky top-24 rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6" data-testid="order-summary">
            <div className="overline mb-3">Your order</div>
            {summary.rows.length === 0 ? (
              <p className="text-rw-ink2 text-sm">Add services to see totals here.</p>
            ) : (
              <>
                <div className="space-y-2">
                  {summary.rows.map((r) => (
                    <div key={r.service_id} className="flex justify-between text-sm">
                      <span>{r.name} <span className="text-rw-ink3">× {r.qty}</span></span>
                      <span>₹{r.subtotal.toFixed(0)}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-rw-ink/10 mt-4 pt-3 text-sm text-rw-ink2 flex justify-between">
                  <span>Pickup & delivery</span>
                  <span>{summary.fee > 0 ? `₹${summary.fee}` : "Free"}</span>
                </div>
                {summary.coupon > 0 && (
                  <div className="text-sm flex justify-between text-rw-sage" data-testid="summary-coupon">
                    <span>Coupon {form.coupon_code}</span>
                    <span>− ₹{summary.coupon.toFixed(0)}</span>
                  </div>
                )}
                {summary.discount > 0 && (
                  <div className="text-sm flex justify-between text-rw-sage" data-testid="summary-discount">
                    <span>Referral discount</span>
                    <span>− ₹{summary.discount.toFixed(0)}</span>
                  </div>
                )}
                {summary.credit > 0 && (
                  <div className="text-sm flex justify-between text-rw-sage" data-testid="summary-credit">
                    <span>Credit applied</span>
                    <span>− ₹{summary.credit.toFixed(0)}</span>
                  </div>
                )}
                <div className="border-t border-rw-ink/10 mt-3 pt-3 font-display text-2xl flex justify-between">
                  <span>Total</span>
                  <span data-testid="summary-total">₹{summary.total.toFixed(0)}</span>
                </div>
                <p className="text-xs text-rw-ink3 mt-4">
                  * Pickup is free on orders above ₹299. Payment via UPI or cash on delivery.
                </p>
              </>
            )}
          </div>
        </aside>
      </div>

      {/* SEO / FAQ content */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 border-t border-rw-ink/10">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="overline mb-3">Why book with RIZ N WASH</div>
            <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight leading-[1.1]">
              Laundry, done the way it should have always been.
            </h2>
            <p className="mt-4 text-rw-ink2 leading-relaxed">
              RIZ N WASH is a doorstep laundry and dry cleaning service built for busy Gurugram households.
              We collect your clothes, wash, iron and fold them in our hygienic studio at Signature Solera, Sector 107, and
              hand them back neatly packed — usually within 48 hours. Every load is sorted by colour and
              fabric, treated for stains, dried in temperature-controlled machines and quality-checked
              before delivery. Free pickup and delivery is included on all orders above ₹299.
            </p>
          </div>
          <div className="md:col-span-7 space-y-6">
            <div>
              <div className="font-display text-xl">What services can I book here?</div>
              <p className="mt-1 text-sm text-rw-ink2 leading-relaxed">
                Everyday wash &amp; iron from ₹19/piece, professional pressing at ₹5 per piece, dry cleaning
                for suits and delicates from ₹149 per garment, and Premium Care for delicate & designer
                pieces starting at ₹125. Mix and match services in a single pickup — you only pay for
                what you actually use.
              </p>
            </div>
            <div>
              <div className="font-display text-xl">How does the pickup slot work?</div>
              <p className="mt-1 text-sm text-rw-ink2 leading-relaxed">
                Choose any two-hour slot between 8 AM and 8 PM. Our runner will WhatsApp you 15 minutes
                before arriving. You can reschedule any pickup up to 30 minutes before the slot with a
                single tap in your confirmation message.
              </p>
            </div>
            <div>
              <div className="font-display text-xl">Which areas do you cover?</div>
              <p className="mt-1 text-sm text-rw-ink2 leading-relaxed">
                We currently serve most of Gurugram including Sector 14, 15, 22, 23, 29, 40–57, DLF Phases
                1–4, Sushant Lok, Golf Course Road, Sohna Road and MG Road. If your sector is not in the
                list, drop us a WhatsApp — we usually add new pincodes every week.
              </p>
            </div>
            <div>
              <div className="font-display text-xl">How can I pay?</div>
              <p className="mt-1 text-sm text-rw-ink2 leading-relaxed">
                Pay via UPI (any app — GPay, PhonePe, Paytm, BHIM) or cash on delivery. Our UPI ID and a
                pay-in-one-tap link are shown on your confirmation screen. You are only charged after your
                clothes are delivered — there are no upfront payments.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
